/**
 * URL builders for navigation throughout the app.
 *
 * New URL format: /project/:projectId/:primaryId?side=:sideId
 * - Primary content ID is in the path
 * - Side panel content is in the `side` query parameter
 * - IDs are self-describing (chat_xxx, asset_xxx, etc.)
 */

/**
 * Build a URL for the new two-column navigation format.
 * @example projectPage("proj_123", "chat_abc") // "/project/proj_123/chat_abc"
 * @example projectPage("proj_123", "chat_abc", "asset_xyz") // "/project/proj_123/chat_abc?side=asset_xyz"
 */
const projectPage = (projectId: string, primaryId?: string, sideId?: string): string => {
  let url = `/project/${projectId}`;
  if (primaryId) {
    url += `/${primaryId}`;
  }
  if (sideId) {
    url += `?side=${sideId}`;
  }
  return url;
};

export const urls = {
  projectPage,

  /**
   * Where a chat opens, for every surface that links to one: the sidebar and
   * homepage lists, the chats and search pages, a notification and a chat
   * citation.
   *
   * The canonical target is the two-column route, the same one
   * `navigation.navigateToPrimary` pushes for an in-app click. Without a
   * project the flat `/chat/:chatId` route is the only thing left to point at;
   * it resolves the project client-side and rewrites itself to the canonical
   * form.
   *
   * `messageId` anchors the link to a turn — chat citations, chat-search hits
   * and chat notifications all deep-link to one, and the fragment is what the
   * chat view scrolls to.
   */
  chatHref: (projectId: string | undefined, chatId: string, messageId?: string): string => {
    const base = projectId ? projectPage(projectId, chatId) : `/chat/${chatId}`;
    return messageId ? `${base}#${messageId}` : base;
  },

  /**
   * Build a URL for opening content in the side panel.
   * Preserves the current primary content.
   */
  withSidePanel: (currentUrl: string, sideId: string): string => {
    const url = new URL(currentUrl, "http://localhost");
    url.searchParams.set("side", sideId);
    return url.pathname + url.search;
  },

  /**
   * Build a URL without the side panel.
   */
  withoutSidePanel: (currentUrl: string): string => {
    const url = new URL(currentUrl, "http://localhost");
    url.searchParams.delete("side");
    const search = url.searchParams.toString();
    return url.pathname + (search ? `?${search}` : "");
  },
};
